import React, { useState, useEffect } from "react";
import { EditButton, DeleteButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Space, Table, Tag, Card, Input } from "antd";
import { SearchOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

interface User {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    createdAt: string;
    lastLoginAt?: string;
    companyName?: string;
}

export const UserList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<User[]>([]);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "auth/users",
    });

    useEffect(() => {
        if (!data?.data) return;

        let users = data.data as User[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            users = users.filter((user: User) =>
                user.username?.toLowerCase().includes(searchLower) ||
                user.email?.toLowerCase().includes(searchLower) ||
                user.firstName?.toLowerCase().includes(searchLower) ||
                user.lastName?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...users].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const columns = [
        {
            title: "Username",
            dataIndex: "username",
            key: "username",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            render: (value: string) => (
                <span className="text-text-secondary">{value}</span>
            ),
        },
        {
            title: "Name",
            key: "fullName",
            render: (_: any, record: User) => (
                <span className="text-text-secondary">
                    {`${record.firstName || ''} ${record.lastName || ''}`.trim() || '-'}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            render: (value: boolean) => (
                <Tag color={value ? "success" : "error"} className="status-tag">
                    {value ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Company",
            dataIndex: "companyName",
            key: "companyName",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "N/A"}</span>
            ),
            responsive: ['lg'] as any,
        },
        {
            title: "Last Login",
            dataIndex: "lastLoginAt",
            key: "lastLoginAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "Never"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_: any, record: User) => (
                <Space size="small">
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Users"
                icon={<TeamOutlined />}
                breadcrumbs={[{ title: "Users" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/users/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by username, email, or name"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>
        </div>
    );
};
